
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { CalendarIcon, Clock, Plus, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { usePermissions } from '@/contexts/unified-permissions';
import { LoadingState } from '@/components/ui/loading-state';
import { Helmet } from 'react-helmet-async';
import { BlogService } from '@/services/blog/blogService';
import type { BlogPost } from '@/services/blog/types';

const Blog = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = usePermissions();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  
  // Fetch blog posts using our service instead of direct supabase query
  const { data: posts, isLoading } = useQuery({
    queryKey: ['blogPosts'],
    queryFn: async () => {
      const fetchedPosts = await BlogService.getPublishedPosts();

      return fetchedPosts;
    },
  });
  
  // Check if user is an admin
  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated) return;
      
      const { data, error } = await supabase.rpc('check_if_user_is_admin');
      if (error) {
        console.error('Error checking admin status:', error);
        return;
      }
      
      setIsAdmin(data === true);
    };
    
    checkAdminStatus();
  }, [isAuthenticated]);
  
  // Handle login/signup
  const handleLogin = () => {
    navigate('/auth/login');
  };

  const handleSignup = () => {
    navigate('/auth/signup');
  };
  
  const handleCreatePost = () => {
    navigate('/blog/admin/create');
  };
  
  const handlePostClick = (slug: string) => {
    navigate(`/blog/${slug}`);
  };
  
  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Blog | The Mat Monster</title>
        <meta name="description" content="Insights, techniques, and stories about Brazilian Jiu-Jitsu from The Mat Monster team." />
        <meta property="og:title" content="Blog | The Mat Monster" />
        <meta property="og:description" content="Insights, techniques, and stories about Brazilian Jiu-Jitsu from The Mat Monster team." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Navigation onLogin={handleLogin} onSignup={handleSignup} />
      
      <div className="container mx-auto px-4 pt-32 pb-16 md:pt-40 md:pb-28">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold">Blog</h1>
          {isAdmin && (
            <Button onClick={handleCreatePost} className="flex items-center gap-2">
              <Plus size={16} />
              Create Post
            </Button>
          )}
        </div>
        
        {isLoading ? (
          <LoadingState title="Loading blog posts" size="lg" />
        ) : posts && posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post: BlogPost) => (
              <div 
                key={post.id}
                onClick={() => handlePostClick(post.slug)}
                className="bg-white border border-neutral-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col h-full"
              >
                {post.featured_image ? (
                  <div className="aspect-video w-full overflow-hidden">
                    <img 
                      src={post.featured_image} 
                      alt={post.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', post.featured_image);
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x450?text=No+Image';
                      }}
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full bg-neutral-100 flex items-center justify-center">
                    <span className="text-neutral-400">No image</span>
                  </div>
                )}
                
                <div className="p-6 flex-1 flex flex-col">
                  <h2 className="text-xl font-bold mb-2">{post.title}</h2>
                  
                  {post.summary && (
                    <p className="text-neutral-600 mb-4 flex-1">{post.summary}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-neutral-500 mt-4">
                    <div className="flex items-center gap-1">
                      <User size={14} />
                      <span>
                        {post.author?.full_name || post.author?.username || 'Unknown'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{post.reading_time_minutes} min read</span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <CalendarIcon size={14} />
                      <span>{post.published_at ? formatDistanceToNow(new Date(post.published_at), { addSuffix: true }) : 'Recently'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <h2 className="text-2xl font-semibold mb-2">No posts yet</h2>
            <p className="text-neutral-600">
              Check back soon for new content!
            </p>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default Blog;
